import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { SrpEntry, SrpEntryList } from "@/api/types";

/**
 * Catálogo de SRPs derivado en DB (GET /api/v1/srps, I1). Fuente única del
 * portal: expone el catálogo completo (incl. steps_count y schedule_blocked)
 * más `auto_execute` por SRP para el badge auto vs manual. Sustituye al lector
 * in-memory `/api/v1/srp-catalog` (deprecado).
 */
export function useSrps(f: SrpFilters = {}) {
  return useQuery({
    queryKey: ["srps", f],
    queryFn: ({ signal }) => api<SrpEntryList>(`/api/v1/srps${toQuery(f)}`, { signal }),
  });
}

/** Valor que agrupa los SRP cuyos pasos no salen de la maquina de AROC.
 *  Tiene que coincidir con `SIN_EQUIPO` de `srp_filtros.py`: es el valor que
 *  viaja en la URL, no una etiqueta de pantalla. */
export const SIN_EQUIPO = "(solo pasos locales)";

export type SrpFacetKey = "equipo" | "client" | "modo" | "seguridad" | "prueba";

export interface SrpFilters {
  q?: string;
  equipo?: string[];
  client?: string[];
  modo?: string[];
  seguridad?: string[];
  prueba?: string[];
}

export interface FacetValue {
  value: string;
  count: number;
}

export type SrpFacets = Record<SrpFacetKey, FacetValue[]>;

const SRP_FACET_KEYS: SrpFacetKey[] = ["equipo", "client", "modo", "seguridad", "prueba"];

/** El backend espera la clave repetida (`equipo=a&equipo=b`), no una lista
 *  separada por comas; mismo criterio que el inventario de credenciales. */
function toQuery(f: SrpFilters) {
  const p = new URLSearchParams();
  if (f.q?.trim()) p.set("q", f.q.trim());
  for (const k of SRP_FACET_KEYS) (f[k] ?? []).forEach((v) => p.append(k, v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Cuantos SRP quedarian al marcar cada valor de cada faceta. */
export function useSrpFacets(f: SrpFilters) {
  return useQuery({
    queryKey: ["srp-facets", f],
    queryFn: ({ signal }) => api<SrpFacets>(`/api/v1/srps/facets${toQuery(f)}`, { signal }),
    staleTime: 30_000,
  });
}


/**
 * Enciende/apaga la ejecución autónoma de un SRP (PATCH, solo super_user).
 * Encender exige safe-idempotent + probado; el backend lo valida.
 */
export function useSetAutoExecute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ protocolId, enabled }: { protocolId: string; enabled: boolean }) =>
      api<SrpEntry>(`/api/v1/srps/${encodeURIComponent(protocolId)}/auto-execute`, {
        method: "PATCH",
        body: { enabled },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["srps"] });
      // Encender un automatico mueve el SRP de "aprobacion" a "automatico":
      // sin esto el riel se queda con el conteo viejo.
      qc.invalidateQueries({ queryKey: ["srp-facets"] });
    },
  });
}
